import { produce } from 'immer';
import { object } from 'zod';
import createCorrelationId from 'orch-communicator/utils/correlation-id';
import createSessionId from 'orch-communicator/utils/session-id';
import { generateUuid } from 'orch-communicator/utils/uuid';
import { Address, PaymentInstrument } from 'types/api/intent';
import {
  ApiResponseCache,
  CardNetwork,
  CountryCode,
  LookupStatus,
  MerchantConfig,
  SRCInfo,
  ToRoute,
  UserType
} from 'types/data';
import { DCFActionCode, MobileNumber } from 'types/emvco';
import { CheckoutData } from 'types/vsb-sdk/checkout';
import { InitData } from 'types/vsb-sdk/init';

const visitId = sessionStorage.getItem('visitId') ?? `VSB_${generateUuid()}`;
sessionStorage.setItem('visitId', visitId);

export type UserProfile = {
  accessToken?: {
    /**
     * When converting a federated idToken to an accessToken, the backend
     * returns an auth_level field indicating the type of stepup the user
     * performed by the third-party SRC system. The values are either:
     *
     * 1. sms_otp/email_otp - The user has already performed a step up and does
     *    not need to perform OTP again.
     * 2. cookie/swk - The user was recognized by cookies, and may need to be
     *    stepped up.
     *
     * The value is relevant when performing user account changes in
     * non-integrated flows. E.g. updating an address from the review screen.
     */
    authLevel?: 'aal0' | 'aal1';
    expiresIn: number;
    token: string;
    isPartial: boolean;
  } | null;
  country?: CountryCode;
  email?: string;
  emailHash?: string;
  flowType?: UserType;
  idToken?: string;
  idValidationSessionId?: string;
  isComplianceTncPassed?: boolean;
  isLocked?: boolean;
  isLoggedInOtpUser?: boolean;
  isNonVisaRecognizedUser?: boolean;
  isPrivacyPolicyPresent?: boolean;
  isRecognizedUser?: boolean;
  locale?: string;
  lookupStatus?: LookupStatus;
  mobileNumber?: MobileNumber;
  xAuthCode?: string;
};
//START GENAI@CHATGPT4
//eslint-disable-next-line
interface MerchantProfile {
  merchantConfig: {
    billingConstraints: {
      acceptedRegions: Record<string, unknown>;
      collectBillingAddress: string;
    };
    clientId: string;
    currencyFormat: string;
    defaultProfile: boolean;
    enableUserDataPrefill: boolean;
    externalClientId: string;
    externalProfileId: string;
    guestCheckout: boolean;
    logo: {
      displayName: string;
    };
    merchantCountryCode: string;
    paymentConstraints: {
      acceptCanadianVISADebit: boolean;
      paymentTypes: {
        paymentType: {
          cardBrand: string;
        }[];
      };
    };
    shippingConstraints: {
      acceptedRegions: Record<string, unknown>;
      collectShipping: boolean;
    };
    tokenizationSetup: {
      enableTokenization: boolean;
      tokenCryptogramTypes: string[];
    };
    widgetStyle: string;
  };
  merchantStatus: string;
  srcInfo: {
    sdks: {
      apiKey: string;
      cardBrand: string;
      domains: string[];
      panEncryptionKey: string;
      panEncryptionKid: string;
      sdkUri: string;
      sdkUriHash: string;
    }[];
  };
  supportedCardNetworks: string[];
}
//END GENAI@CHATGPT4
//START GENAI@CHATGPT4
export type QueryAttributes = {
  cardBrands: string;
  country: string;
  dpaClientId: string;
  dpaId: string;
  dpaProfileId: string;
  locale: string;
};
//END GENAI@CHATGPT4
export type AddCardInfo = {
  cardSuffix: string;
  networkId: CardNetwork;
  panJWE: string;
};
export type FlowState = {
  addCardInfo?: AddCardInfo;
  dcfActionCode?: DCFActionCode;
  grant?: boolean;
  toRoute?: ToRoute;
  why?: string;
};
export interface SdkLoadInfo {
  loadedSdk?: object;
}
export type CommunicatorState = {
  config: {
    checkout?: CheckoutData;
    init?: InitData;
  };
  flow: FlowState;
  queryAttributes: QueryAttributes | null;
  meta: {
    cardIdMapping: Record<string, string | undefined>;
    correlationId: string;
    isBiometricsEnabledDevice: boolean;
    isCheckoutDataReady: boolean;
    isSplunkLoggingEnabled: boolean;
    orgId: string | null;
    otpProvider?: string;
    selectedCardId: string;
    sessionId: string;
    thmProfileHint: string | null;
    vIntentId?: string;
    visitId: string;
  };
  merchantProperties: MerchantConfig | null;
  profile: {
    paymentInstrument?: PaymentInstrument[];
    shippingAddress?: Address[];
  };
  sdksReady: boolean;
  supportedCardNetworks: string[];
  srcInfo: SRCInfo | null;
  uatStatus: boolean;
  srcSdks: {
    loadedSdk: object;
    apiResponseCache: ApiResponseCache;
    preferredNetwork: CardNetwork | '';
  };
  user: UserProfile | null;
};

let state: CommunicatorState = {
  config: {},
  flow: {},
  merchantProperties: null /* window.VSB_GLOBALS.merchantConfig, */,
  meta: {
    cardIdMapping: {},
    // default correlationId is overwritten in init()
    correlationId: createCorrelationId(),
    isBiometricsEnabledDevice: false,
    isCheckoutDataReady: false,
    isSplunkLoggingEnabled: false,
    orgId: null /* window.VSB_GLOBALS.orgId, */,
    selectedCardId: '',
    sessionId: createSessionId(),
    thmProfileHint: null,
    visitId
  },
  profile: {},
  queryAttributes: null /* window.VSB_GLOBALS.queryAttributes, */,
  sdksReady: false,
  srcInfo: null /* window.VSB_GLOBALS.merchantConfig.srcInfo, */,
  srcSdks: {
    loadedSdk: {
      VISA: object,
      MASTERCARD: object
    },
    apiResponseCache: {
      checkout: {},
      completeIdentityValidation: '',
      getSrcProfile: {},
      identityLookUp: {},
      idTokens: {},
      init: {},
      initData: {},
      initiateIdentityValidation: {},
      isRecognized: {}
    },
    preferredNetwork: ''
  },
  supportedCardNetworks: [],
  uatStatus: false,
  user: null
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let devTools: any = null;

if (process.env.NODE_ENV !== 'production') {
  devTools = window.__REDUX_DEVTOOLS_EXTENSION__?.connect({
    name: `VSB SDK (${window.location.origin})`
  });

  devTools?.init(state);
}

const subscribers: (() => unknown)[] = [];

export function getState() {
  return state;
}

type ChangeHandler<StatePartial> = (
  currentState: StatePartial,
  previousState: StatePartial
) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function subscribe<S extends (s: CommunicatorState) => any>(
  selector: S,
  onChange: ChangeHandler<ReturnType<S>>
) {
  let currentState = selector(getState());

  const subscriber = () => {
    const nextState = selector(getState());

    if (currentState !== nextState) {
      const previousState = currentState;
      currentState = nextState;
      onChange(currentState, previousState);
    }
  };

  subscribers.push(subscriber);

  return () => {
    const index = subscribers.indexOf(subscriber);
    subscribers.splice(index, 1);
  };
}

function notifySubscribers() {
  subscribers.forEach(subscriber => subscriber());
}

function produceAndNotify(type: string, recipe: (state: CommunicatorState) => void) {
  state = produce(state, recipe);
  notifySubscribers();

  if (process.env.NODE_ENV !== 'production') {
    devTools?.send(type, state);
  }
}

export function reset() {
  produceAndNotify('reset', draft => {
    draft.flow = {};
    draft.user = null;
    draft.meta = {
      ...draft.meta,
      cardIdMapping: {},
      correlationId: createCorrelationId(),
      isCheckoutDataReady: false,
      vIntentId: undefined
    };

    draft.profile = {};
  });
}

export function resetUserProfile() {
  produceAndNotify('resetUserProfile', draft => {
    draft.user = null;
  });
}

//START GENAI@GHCOPILOT
export function resetApiResponseCache() {
  produceAndNotify('resetApiResponseCache', draft => {
    draft.srcSdks.apiResponseCache = {
      ...draft.srcSdks.apiResponseCache,
      checkout: {},
      completeIdentityValidation: '',
      getSrcProfile: {},
      identityLookUp: {},
      idTokens: {},
      init: {},
      initData: {},
      initiateIdentityValidation: {},
      isRecognized: {}
    };
  });
}
//END GENAI@GHCOPILOT

export function saveFlow(flow: Partial<CommunicatorState['flow']>) {
  produceAndNotify('saveFlow', draft => {
    draft.flow = { ...state.flow, ...flow };
  });
}

export function saveInitData(data: InitData) {
  produceAndNotify('saveInitData', draft => {
    draft.config.init = data;
  });
}

export function saveMeta(meta: Partial<CommunicatorState['meta']>) {
  produceAndNotify('saveMeta', draft => {
    draft.meta = { ...state.meta, ...meta };
  });
}

export function saveProfile(profile: Partial<CommunicatorState['profile']>) {
  produceAndNotify('saveProfile', draft => {
    draft.profile = { ...state.profile, ...profile };
  });
}
// START GENAI@CHATGPT4

export function saveSdkLoadInfo(info: SdkLoadInfo) {
  // END GENAI@CHATGPT4
  produceAndNotify('srcSdks', draft => {
    draft.srcSdks.loadedSdk = { ...state.srcSdks.loadedSdk, ...info };
  });
}

export function saveQueryAttributes(queryAttributes: QueryAttributes) {
  produceAndNotify('queryAttributes', draft => {
    draft.queryAttributes = { ...state.queryAttributes, ...queryAttributes };
  });
}

export function saveUser(user: UserProfile) {
  produceAndNotify('saveUser', draft => {
    draft.user = { ...state.user, ...user };
  });
}

//START GENAI@COPILOT
export function saveApiResponseCache(apiResponseCache: ApiResponseCache) {
  produceAndNotify('apiResponseCache', draft => {
    draft.srcSdks.apiResponseCache = { ...state.srcSdks.apiResponseCache, ...apiResponseCache };
  });
}
//END GENAI@COPILOT

//START GENAI@GHCOPILOT
export function savePreferredNetwork(preferredNetwork: CardNetwork | '') {
  produceAndNotify('savePreferredNetwork', draft => {
    draft.srcSdks.preferredNetwork = preferredNetwork;
  });
}
//END GENAI@GHCOPILOT

export function saveMerchantProperties(window: Window) {
  produceAndNotify('saveMerchantProperties', draft => {
    draft.merchantProperties = window.VSB_GLOBALS.merchantConfig;
    draft.queryAttributes = window.VSB_GLOBALS.queryAttributes;
    draft.supportedCardNetworks = window.VSB_GLOBALS.supportedCardNetworks;
    draft.srcInfo = window.VSB_GLOBALS.merchantConfig.srcInfo;
    draft.meta.orgId = window.VSB_GLOBALS.orgId;
  });
}

//START GENAI@GHCOPILOT
export function saveSupportedCardNetworks(cardBrands: string[]) {
  produceAndNotify('saveSupportedCardNetworks', draft => {
    draft.supportedCardNetworks = cardBrands;
  });
}
//END GENAI@GHCOPILOT

export function saveSdksReady() {
  produceAndNotify('saveSdksReady', draft => {
    draft.sdksReady = true;
  });
}

export function saveUatStatus(uatStatus: boolean) {
  produceAndNotify('saveUatStatus', draft => {
    // Sticky for the session: once enabled, later events (e.g. a stale/default
    // resend from the host) must not flip it back off.
    draft.uatStatus = draft.uatStatus || uatStatus;
  });
}
